import { useState } from 'react';

import { useDeletePolicy } from '~entities/policies/api/delete-policy';
import type { PolicyDetails } from '~entities/policies/modal/policy';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shared/shadcn/ui/dialog';
import { Label } from '~/shared/shadcn/ui/label';
import { Button } from '~shared/shadcn/ui/button';
import { Input } from '~shared/shadcn/ui/input';

interface DeletePolicyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPolicies: Array<PolicyDetails>;
  onClose: () => void;
  isBulkDelete?: boolean;
  onDelete?: () => void;
}

export const DeletePolicyDialog = ({
  open,
  onOpenChange,
  selectedPolicies = [],
  onClose,
  isBulkDelete = false,
  onDelete,
}: DeletePolicyDialogProps) => {
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const deleteMutation = useDeletePolicy();

  const handleClose = () => {
    setDeleteConfirmText('');
    onClose();
  };

  const hasAssociations = selectedPolicies.some(
    (policy) =>
      (policy.class.length ?? 0) > 0 || (policy.event.length ?? 0) > 0,
  );

  const associationDetails = selectedPolicies
    .filter(
      (policy) =>
        (policy.class.length ?? 0) > 0 || (policy.event.length ?? 0) > 0,
    )
    .map((policy) => ({
      name: policy.name,
      classCount: policy.class.length ?? 0,
      eventCount: policy.event.length ?? 0,
    }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Delete {isBulkDelete ? 'Policies' : 'Policy'}
          </DialogTitle>
        </DialogHeader>

        <DialogDescription>
          {hasAssociations && (
            <div className='mb-4 rounded-md bg-yellow-50 p-4'>
              <p className='text-yellow-800'>
                Warning: The following{' '}
                {isBulkDelete ? 'policies have' : 'policy has'} associations:
              </p>
              <ul className='mt-2 list-disc pl-4'>
                {associationDetails.map((policy) => (
                  <li key={policy.name}>
                    <span className='font-medium'>{policy.name}</span>:{' '}
                    {policy.classCount}{' '}
                    {policy.classCount === 1 ? 'class' : 'classes'} and{' '}
                    {policy.eventCount}{' '}
                    {policy.eventCount === 1 ? 'event' : 'events'}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p>Are you sure you want to delete? This action cannot be undone.</p>
        </DialogDescription>

        <div className='mt-4'>
          <Label htmlFor='confirmDelete'>
            Type &quot;delete&quot; to confirm:
          </Label>
          <Input
            id='confirmDelete'
            value={deleteConfirmText}
            onChange={(e) => {
              setDeleteConfirmText(e.target.value);
            }}
            className='mt-2'
          />
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant='destructive'
            disabled={deleteConfirmText !== 'delete'}
            onClick={() => {
              if (isBulkDelete && onDelete) {
                onDelete();
              } else {
                deleteMutation.mutate({
                  policyId: selectedPolicies[0]?._id ?? '',
                });
              }
              handleClose();
            }}
          >
            {isBulkDelete ? 'Delete Policies' : 'Delete Policy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
